import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '@/components/common/Button';
import Card, { CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/common/Card';
import Modal from '@/components/common/Modal';
import { Loader2, User, Lock, Eye, EyeOff, AlertCircle, Mail, Phone } from 'lucide-react';
import { logger } from '@/lib/logger';

// Esquema de validación con Zod
const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'El nombre de usuario es requerido')
    .min(3, 'El nombre de usuario debe tener al menos 3 caracteres'),
  password: z
    .string()
    .min(1, 'La contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data);
    } catch (error) {
      logger.error('Error al iniciar sesión:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-background dark:bg-gray-900 dark:text-gray-100 p-4 animate-fadeIn">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
            <div className="mx-auto h-20 w-20 mb-4">
                <img
                  src="/logo.png"
                  alt="MundiRep"
                  className="w-full h-full object-contain"
                />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
                Bienvenido
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
                Ingresa tus credenciales para acceder al sistema
            </p>
        </div>

        <Card className="border-border/50 shadow-2xl shadow-primary/5 backdrop-blur-sm bg-card/90">
          <CardHeader>
            <CardTitle className="text-center text-lg">Iniciar Sesión</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start space-x-3">
                   <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                   <p className="text-sm text-destructive font-medium">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="username">
                  Usuario
                </label>
                <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      {...register('username')}
                      id="username"
                      type="text"
                      className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.username ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                      }`}
                      placeholder="nombre.usuario"
                    />
                </div>
                {errors.username && (
                  <p className="text-xs text-destructive font-medium">
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="password">
                  Contraseña
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      {...register('password')}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 pr-10 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.password ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 md:top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 md:h-4 md:w-4" />
                      ) : (
                        <Eye className="h-5 w-5 md:h-4 md:w-4" />
                      )}
                    </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive font-medium">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="remember"
                      className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor="remember"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-muted-foreground"
                    >
                      Recordarme
                    </label>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="text-sm font-medium text-primary hover:underline underline-offset-4"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                loading={isLoading}
              >
                Ingresar al Sistema
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center border-t border-border pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="font-medium text-primary hover:underline underline-offset-4">
                Regístrate aquí
              </Link>
            </p>
          </CardFooter>
        </Card>
        
        <p className="text-center text-xs text-muted-foreground mt-8">
          &copy; {new Date().getFullYear()} Sistema Mundi. Versión 2.0
        </p>
      </div>

      {/* Modal de recuperación de contraseña */}
      <Modal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        title="Recuperar Contraseña"
        size="sm"
      >
        <div className="space-y-4">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <p className="text-muted-foreground">
              Para restablecer tu contraseña, por favor contacta al administrador del sistema.
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Correo electrónico</p>
                <a
                  href="mailto:admin@mundirep.com"
                  className="text-sm text-primary hover:underline"
                >
                  admin@mundirep.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Teléfono</p>
                <p className="text-sm text-muted-foreground">Contacta a tu supervisor directo</p>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <Button
              className="w-full"
              onClick={() => setShowForgotPasswordModal(false)}
            >
              Entendido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Login;